import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, DeleteIcon, RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DeleteWithReasonModal from './DeleteWithReasonModal';
import RecadoModal from './RecadoModal';
import TarefaModal, { TAREFA_PERIODICIDADE, TAREFA_STATUS } from './TarefaModal';
import { confirmarRecebimentoTarefa, deleteTarefa, listTarefas, updateTarefa } from '@/services/tarefaService';
import { getMyUserKeyFromStorage } from './sessionUserKey';
import { useUser } from '@/components/Context/UserContext';
import { deleteRecado, listRecados, marcarRecadosCaixaVista, responderRecado, updateRecado } from '@/services/recadoService';
import { listNotificacoes, marcarNotificacaoLida } from './notificacaoInAppService';
import { Spinner } from '@/components';

const TAB = {
  TAREFA: 'tarefa',
  RECADO: 'recado',
};

const LIMIT = 5;

const STATUS_LABEL = {
  PENDENTE: 'Pendente',
  EM_PROGRESSO: 'Em progresso',
  CONCLUIDA: 'Concluída',
};

const STATUS_COLOR = {
  PENDENTE: 'red',
  EM_PROGRESSO: 'yellow',
  CONCLUIDA: 'green',
};

const PERIODICIDADE_LABEL = {
  [TAREFA_PERIODICIDADE.NENHUMA]: '',
  [TAREFA_PERIODICIDADE.DIARIA]: 'Diária',
  [TAREFA_PERIODICIDADE.SEMANAL]: 'Semanal',
  [TAREFA_PERIODICIDADE.MENSAL]: 'Mensal',
};

const DIAS_SEMANA_LABEL = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatDiasSemana(dias) {
  if (!Array.isArray(dias) || dias.length === 0) return '';
  return dias
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DIAS_SEMANA_LABEL[d] ?? d)
    .join(', ');
}

function formatDate(dateString) {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '';
  }
}

function formatDateTime(dateString) {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return '';
  }
}

function Paginacao({ page, pages, total, onPageChange, isLoading }) {
  return (
    <HStack justify="center" pt={3} spacing={2}>
      <IconButton
        aria-label="Página anterior"
        size="xs"
        icon={<ChevronLeftIcon />}
        isDisabled={page <= 1 || isLoading}
        onClick={() => onPageChange(page - 1)}
      />
      <Text fontSize="xs" color="gray.600">
        {page} / {pages}
        {total != null ? ` (${total})` : ''}
      </Text>
      <IconButton
        aria-label="Próxima página"
        size="xs"
        icon={<ChevronRightIcon />}
        isDisabled={page >= pages || isLoading}
        onClick={() => onPageChange(page + 1)}
      />
    </HStack>
  );
}

export default function TarefaRecadoSidebar({ clinicaId, onBandejaCountsRefresh }) {
  const toast = useToast();
  const [tab, setTab] = useState(TAB.TAREFA);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const [tarefas, setTarefas] = useState([]);
  const [recados, setRecados] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [paginacao, setPaginacao] = useState(null);

  const {
    isOpen: isTarefaModalOpen,
    onOpen: onOpenTarefaModal,
    onClose: onCloseTarefaModal,
  } = useDisclosure();
  const {
    isOpen: isRecadoModalOpen,
    onOpen: onOpenRecadoModal,
    onClose: onCloseRecadoModal,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    isOpen: isRespostaOpen,
    onOpen: onOpenResposta,
    onClose: onCloseResposta,
  } = useDisclosure();
  const [respostaTarget, setRespostaTarget] = useState(null);
  const [respostaText, setRespostaText] = useState('');
  const [respostaSaving, setRespostaSaving] = useState(false);
  const [lembretesNaoLidos, setLembretesNaoLidos] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const { user } = useUser();
  const canUse = !!clinicaId;
  const myUserKey = useMemo(() => getMyUserKeyFromStorage(), []);

  const carregarLembretes = async () => {
    if (!clinicaId || tab !== TAB.TAREFA) return;
    try {
      const r = await listNotificacoes({ clinicaId, unread: true, limit: 15 });
      setLembretesNaoLidos(Array.isArray(r?.data) ? r.data : []);
    } catch {
      setLembretesNaoLidos([]);
    }
  };

  const fetchAll = async (pageNum = 1) => {
    if (!canUse) return;
    setLoading(true);
    setPage(pageNum);
    try {
      if (tab === TAB.TAREFA) {
        const result = await listTarefas({
          clinicaId,
          status: statusFilter || undefined,
          q: search.trim() || undefined,
          page: pageNum,
          limit: LIMIT,
        });
        setTarefas(Array.isArray(result?.data) ? result.data : []);
        setPaginacao(result?.pagination || null);
      } else {
        const result = await listRecados({
          clinicaId,
          box: 'all',
          status: statusFilter || undefined,
          q: search.trim() || undefined,
          page: pageNum,
          limit: LIMIT,
        });
        setRecados(Array.isArray(result?.data) ? result.data : []);
        setPaginacao(result?.pagination || null);
        try {
          await marcarRecadosCaixaVista(clinicaId);
        } catch {
          /* ignore */
        }
        onBandejaCountsRefresh?.();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar:', e);
      toast({
        title: 'Erro',
        description: e?.message || 'Não foi possível carregar',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
    if (tab === TAB.TAREFA) {
      carregarLembretes();
    }
  };

  useEffect(() => {
    fetchAll(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, tab, statusFilter]);

  useEffect(() => {
    if (canUse && tab === TAB.TAREFA) {
      carregarLembretes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, tab]);

  const visibleItems = useMemo(
    () => (tab === TAB.TAREFA ? tarefas : recados),
    [tab, tarefas, recados]
  );

  const openDelete = (type, item) => {
    setDeleteTarget({ type, item });
    onOpenDelete();
  };

  const confirmDelete = async (reason) => {
    const target = deleteTarget;
    if (!target?.item?._id) return;
    if (target.type === TAB.TAREFA) {
      await deleteTarefa(target.item._id, reason);
      toast({ title: 'Tarefa excluída', status: 'success', duration: 2500, isClosable: true });
    } else {
      await deleteRecado(target.item._id, reason);
      toast({ title: 'Recado excluído', status: 'success', duration: 2500, isClosable: true });
    }
    setDeleteTarget(null);
    onCloseDelete();
    await fetchAll(page);
    onBandejaCountsRefresh?.();
  };

  const updateStatus = async (type, id, nextStatus) => {
    try {
      if (type === TAB.TAREFA) {
        await updateTarefa(id, { status: nextStatus });
      } else {
        await updateRecado(id, { status: nextStatus });
      }
      await fetchAll(page);
      onBandejaCountsRefresh?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao atualizar status:', e);
      toast({
        title: 'Erro ao atualizar',
        description: e?.message || 'Não foi possível atualizar o status',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    }
  };

  const confirmarRecebimento = async (id) => {
    try {
      const updated = await confirmarRecebimentoTarefa(id);
      setTarefas((prev) => prev.map((t) => (t._id === id ? updated : t)));
      toast({ title: 'Recebimento confirmado', status: 'success', duration: 2500, isClosable: true });
      onBandejaCountsRefresh?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao confirmar recebimento:', e);
      toast({
        title: 'Erro',
        description: e?.message || 'Não foi possível confirmar',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    }
  };

  const marcarLembreteLido = async (notifId) => {
    try {
      await marcarNotificacaoLida(notifId);
      setLembretesNaoLidos((prev) => prev.filter((n) => n._id !== notifId));
      onBandejaCountsRefresh?.();
    } catch (e) {
      toast({
        title: 'Erro',
        description: e?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const openResposta = (recado) => {
    setRespostaTarget(recado);
    setRespostaText(recado.resposta || '');
    onOpenResposta();
  };

  const salvarResposta = async () => {
    if (!respostaTarget?._id) return;
    setRespostaSaving(true);
    try {
      await responderRecado(respostaTarget._id, respostaText);
      toast({ title: 'Resposta enviada', status: 'success', duration: 2500, isClosable: true });
      onCloseResposta();
      setRespostaTarget(null);
      setRespostaText('');
      await fetchAll(page);
    } catch (e) {
      toast({ title: 'Erro ao enviar resposta', description: e?.message, status: 'error', duration: 4500, isClosable: true });
    } finally {
      setRespostaSaving(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') fetchAll(1);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" maxH="calc(100vh - 40px)">
      <Box p={3}>
        <Flex align="center" justify="space-between" gap={2}>
          <Heading fontSize="md" color="dashboard">Nova tarefa/recado</Heading>
          <HStack spacing={1}>
            <Tooltip label="Atualizar">
              <IconButton
                aria-label="Atualizar"
                size="sm"
                icon={<RepeatIcon />}
                onClick={() => fetchAll(1)}
                isLoading={loading}
              />
            </Tooltip>
            <Tooltip label={collapsed ? 'Expandir controles' : 'Comprimir controles'}>
              <IconButton
                aria-label={collapsed ? 'Expandir' : 'Comprimir'}
                size="sm"
                variant="outline"
                icon={collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                onClick={() => setCollapsed((c) => !c)}
              />
            </Tooltip>
          </HStack>
        </Flex>

        {!collapsed && (
          <>
            <ButtonGroup size="sm" isAttached variant="solid" mt={3} w="full">
              <Button
                flex="1"
                colorScheme={tab === TAB.TAREFA ? 'teal' : 'gray'}
                variant={tab === TAB.TAREFA ? 'solid' : 'outline'}
                onClick={() => { setTab(TAB.TAREFA); setSearch(''); }}
              >
                Tarefa
              </Button>
              <Button
                flex="1"
                colorScheme={tab === TAB.RECADO ? 'teal' : 'gray'}
                variant={tab === TAB.RECADO ? 'solid' : 'outline'}
                onClick={() => { setTab(TAB.RECADO); setSearch(''); }}
              >
                Recado
              </Button>
            </ButtonGroup>

            <HStack mt={3}>
              <Button
                size="sm"
                colorScheme="blue"
                w="full"
                onClick={tab === TAB.TAREFA ? onOpenTarefaModal : onOpenRecadoModal}
                isDisabled={!canUse}
              >
                {tab === TAB.TAREFA ? 'Criar tarefa' : 'Enviar recado'}
              </Button>
            </HStack>

            {!canUse ? (
              <Box mt={3} p={3} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
                <Text fontSize="sm" fontWeight="semibold">Selecione uma clínica na Agenda.</Text>
              </Box>
            ) : (
              <>
                <Divider my={3} />
                <HStack spacing={3} align="end">
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Filtro</FormLabel>
                    <Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="">Todos</option>
                      <option value={TAREFA_STATUS.DODIA}>Do dia</option>
                      <option value={TAREFA_STATUS.PENDENTE}>Pendente</option>
                      <option value={TAREFA_STATUS.EM_PROGRESSO}>Em progresso</option>
                      <option value={TAREFA_STATUS.CONCLUIDA}>Concluída</option>
                    </Select>
                  </FormControl>
                </HStack>
                <InputGroup mt={2} size="sm">
                  <Input
                    placeholder="Pesquisar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Buscar"
                      icon={<SearchIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => fetchAll(1)}
                      isLoading={loading}
                    />
                  </InputRightElement>
                </InputGroup>
              </>
            )}
          </>
        )}
      </Box>

      {canUse && tab === TAB.TAREFA && lembretesNaoLidos.length > 0 ? (
        <>
          <Divider />
          <Box px={3} py={2} bg="purple.50" borderBottomWidth="1px">
            <Text fontSize="xs" fontWeight="bold" color="purple.800" mb={1}>
              Lembretes ({lembretesNaoLidos.length})
            </Text>
            <VStack align="stretch" spacing={1}>
              {lembretesNaoLidos.map((n) => (
                <Flex key={n._id} justify="space-between" gap={2} align="center">
                  <Text fontSize="xs" noOfLines={2} flex="1">
                    {n.titulo}
                  </Text>
                  <Button size="xs" variant="outline" onClick={() => marcarLembreteLido(n._id)}>
                    OK
                  </Button>
                </Flex>
              ))}
            </VStack>
          </Box>
        </>
      ) : null}

      <Divider />
      <Paginacao
        page={page}
        pages={paginacao?.pages}
        total={paginacao?.total}
        onPageChange={(p) => fetchAll(p)}
        isLoading={loading}
      />

      <Box p={3} overflow="auto" maxH={collapsed ? 'calc(100vh - 120px)' : 'calc(100vh - 260px)'}>
        {!canUse ? null : loading ? (
          <Spinner />
        ) : visibleItems.length === 0 ? (
          <Text fontSize="sm" color="gray.500">Nenhum item.</Text>
        ) : (
          <>
            <VStack align="stretch" spacing={2}>
              {tab === TAB.TAREFA
                ? visibleItems.map((t) => {
                  const souResponsavel = myUserKey && t.responsavelUserKey === myUserKey;
                  const perLabel = PERIODICIDADE_LABEL[t.periodicidade] || '';
                  return (
                    <Box
                      key={t._id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      borderLeftWidth="6px"
                      borderLeftColor={`${STATUS_COLOR[t.status] || 'gray'}.400`}
                    >
                      <Flex justify="space-between" gap={2}>
                        <Box flex="1" minW="0">
                          <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{t.titulo}</Text>
                          {t.descricao ? (
                            <Text fontSize="xs" color="gray.600" noOfLines={2}>{t.descricao}</Text>
                          ) : null}
                          <HStack mt={1} spacing={2} wrap="wrap">
                            <Badge colorScheme={STATUS_COLOR[t.status] || 'gray'}>{STATUS_LABEL[t.status] || t.status}</Badge>
                            {perLabel ? (
                              <Badge colorScheme="purple" variant="outline">{perLabel}</Badge>
                            ) : null}
                            {t.data ? (
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(t.data)}{t.horario ? ` • ${t.horario}` : ''}
                              </Text>
                            ) : null}
                            {t.periodicidade === TAREFA_PERIODICIDADE.SEMANAL && t.diasSemana?.length > 0 ? (
                              <Text fontSize="xs" color="purple.600">{formatDiasSemana(t.diasSemana)}</Text>
                            ) : null}
                          </HStack>
                          {t.enviadoPara?.name ? (
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              Para: {t.enviadoPara.name}
                            </Text>
                          ) : null}
                          {t.criadoPor?.name ? (
                            <Text fontSize="xs" color="gray.500" mt={0.5}>
                              Criado por: {t.criadoPor.name}
                            </Text>
                          ) : null}
                          {t.recebidoEm ? (
                            <Text fontSize="xs" color="green.600" mt={0.5}>
                              Recebido {formatDateTime(t.recebidoEm)}
                              {t.recebidoPorLabel ? ` — ${t.recebidoPorLabel}` : ''}
                            </Text>
                          ) : null}
                          {souResponsavel && !t.recebidoEm && t.status !== TAREFA_STATUS.CONCLUIDA ? (
                            <Button
                              mt={2}
                              size="xs"
                              colorScheme="teal"
                              variant="solid"
                              onClick={() => confirmarRecebimento(t._id)}
                            >
                              Confirmar recebimento
                            </Button>
                          ) : null}
                        </Box>
                        {t.status !== 'CONCLUIDA' && (
                          <VStack spacing={1} align="stretch" minW="110px">
                            <Select
                              size="xs"
                              value={t.status}
                              onChange={(e) => updateStatus(TAB.TAREFA, t._id, e.target.value)}
                            >
                              <option value={TAREFA_STATUS.EM_PROGRESSO}>Em progresso</option>
                              <option value={TAREFA_STATUS.CONCLUIDA}>Concluída</option>
                            </Select>
                            {String(t.enviadoPara?._id) !== String(user?._id) ? (
                              <IconButton
                                aria-label="Excluir tarefa"
                                size="xs"
                                variant="outline"
                                colorScheme="red"
                                icon={<DeleteIcon />}
                                onClick={() => openDelete(TAB.TAREFA, t)}
                              />
                            ) : null}
                          </VStack>
                        )}
                      </Flex>
                    </Box>
                  );
                })
                : visibleItems.map((r) => (
                  <Box
                    key={r._id}
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                    borderLeftWidth="6px"
                    borderLeftColor={`${STATUS_COLOR[r.status] || 'gray'}.400`}
                  >
                    <Flex justify="space-between" gap={2}>
                      <Box flex="1" minW="0">
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
                          {r.enviadoPara?.name || r.enviadoPara?.email || '—'}
                        </Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={3}>{r.texto}</Text>
                        <HStack mt={1} spacing={2} wrap="wrap">
                          <Badge colorScheme={STATUS_COLOR[r.status] || 'gray'}>{STATUS_LABEL[r.status] || r.status}</Badge>

                          <Text fontSize="xs" color="gray.600">{formatDate(r.dataFinal)}{r.horario ? ` • ${r.horario}` : ''}</Text>
                        </HStack>
                        {r.criadoPor?.name ? (
                          <Text fontSize="xs" color="gray.500" mt={0.5}>
                            Enviado por: {r.criadoPor.name}
                          </Text>
                        ) : null}
                        {r.resposta ? (
                          <>
                            <Divider mt={1} mb={1} />
                            <Text fontSize="xs" color="green" mt={0.5}>
                              Respondido em {formatDate(r.dataResposta)} <br /> - {r.resposta}
                            </Text>
                          </>
                        ) : null}
                      </Box>
                      {r.status !== 'CONCLUIDA' && (
                        <VStack spacing={1} align="stretch" minW="110px">
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => openResposta(r)}
                          >
                            Resposta
                          </Button>
                          {String(r.enviadoPara?._id) !== String(user?._id) ? (
                            <IconButton
                              aria-label="Excluir recado"
                              size="xs"
                              variant="outline"
                              colorScheme="red"
                              icon={<DeleteIcon />}
                              onClick={() => openDelete(TAB.RECADO, r)}
                            />
                          ) : null}
                        </VStack>
                      )}
                    </Flex>
                  </Box>
                ))}
            </VStack>
          </>
        )}
      </Box>

      <TarefaModal
        isOpen={isTarefaModalOpen}
        onClose={onCloseTarefaModal}
        clinicaId={clinicaId}
        onCreated={() => fetchAll(1)}
      />
      <RecadoModal
        isOpen={isRecadoModalOpen}
        onClose={onCloseRecadoModal}
        clinicaId={clinicaId}
        onCreated={async () => {
          await fetchAll(1);
          onBandejaCountsRefresh?.();
        }}
      />

      <Modal isOpen={isRespostaOpen} onClose={onCloseResposta} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Resposta ao recado</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {respostaTarget?.texto ? (
              <Text fontSize="xs" color="gray.500" mb={3} noOfLines={3}>
                {respostaTarget.texto}
              </Text>
            ) : null}
            <Textarea
              placeholder="Digite sua resposta..."
              value={respostaText}
              onChange={(e) => setRespostaText(e.target.value)}
              rows={5}
              maxLength={2000}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={onCloseResposta} isDisabled={respostaSaving}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={salvarResposta}
              isLoading={respostaSaving}
              isDisabled={!respostaText.trim()}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DeleteWithReasonModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setDeleteTarget(null);
          onCloseDelete();
        }}
        title={deleteTarget?.type === TAB.TAREFA ? 'Excluir tarefa' : 'Excluir recado'}
        subjectLabel={
          deleteTarget?.type === TAB.TAREFA
            ? deleteTarget?.item?.titulo
            : deleteTarget?.item?.texto
        }
        onConfirm={confirmDelete}
      />
    </Box>
  );
}
